import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl, getLocale } from 'umi';
import {
  Modal,
  Button,
  Form,
  Radio,
  RadioGroup,
  Checkbox,
  Switch,
  // InputNumber,
  // Bubble,
} from 'tea-component';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { transformDateType2dateTypely } from '@/pages/intelligence/utils/common';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { addFormatMessageSafelyHandler } from '../../utils';
import { CalcMethod } from '../../enums';
import {
  ALL_ONLY_GLOBAL_DATA_METRICS,
  ALL_ONLY_MONTHLY_DATA_METRICS,
  ALL_DATASOURCES_METRICS,
  FLATTEN_METRICS_NAMES_MAPPING,
} from '../../constants';
import type { IBaseProps, IDateType, IDateTypely } from '@/pages/intelligence/types/common';
import './index.scss';
import SteamIcon from '@/pages/intelligence/assets/svg/dataSource/steam-icon.svg';
// import NewzooIcon from '@/pages/home/assets/svg/dataSource/databarin.svg';
import NewzooIcon from '@/pages/intelligence/assets/svg/dataSource/databarin-gray.svg';
import MscienceIcon from '@/pages/intelligence/assets/svg/dataSource/mscience-bg.svg';
import NpdIcon from '@/pages/intelligence/assets/svg/dataSource/npd-icon.svg';
const metricsValues = [...ALL_DATASOURCES_METRICS];
const getIcon = (value:string)=>{
  switch (value) {
    case "steamcharts###pcu":
    case "steamcharts###acu":
      return SteamIcon;
    case "newzoo###mau":
      return NewzooIcon;
    case "mscience###revenue":
    case "mscience###units":
    case "mscience###ast":
    case "mscience###arppu":
      return MscienceIcon;
    case "npd###revenue":
      return NpdIcon;
    default:
      return '';
  }
};
const metricsOptions = metricsValues.map((item) => ({
  text: FLATTEN_METRICS_NAMES_MAPPING[item],
  value: item,
  icon: getIcon(item)
}));

function getDownloadDateTypes(defaultDateTypes?: IDateType[]): IDateTypely[] {
  if (!Array.isArray(defaultDateTypes)) return ['daily'];
  return defaultDateTypes.map((item) => transformDateType2dateTypely(item));
}

interface DownloadModal extends IBaseProps {
  downloadLogConfigKey?: string;
  defaultDateTypes?: IDateType[];
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm?: (payload: any) => Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
}

export default function DownloadModal(props: DownloadModal) {
  const { downloadLogConfigKey, defaultDateTypes, visible, setVisible, onConfirm, onCancel, onClose } = props;

  const { formatMessage } = useIntl();
  const formatMessageSafely = addFormatMessageSafelyHandler(formatMessage);

  const locale = getLocale();
  const isZH = locale === 'zh-CN';
  const isEN = locale === 'en-US';

  const initialDateTypes = getDownloadDateTypes(defaultDateTypes);

  const [downloadConfig, setDownloadConfig] = useState<any>({
    metrics: [...metricsValues],
    release_date_show: false,
    company_show: false,
    genre_show: false,
    ratio_show: true,
    date_method: CalcMethod.Aggregated,
    date_types: initialDateTypes,
    region_method: CalcMethod.Aggregated,
    data_amount: 'export_all_data',
    data_amount_num: 0,
    file_format: '0',
  });

  const isDownloadConfigValid = (() => {
    if (!downloadConfig?.metrics?.length) return false;
    if (downloadConfig?.date_method === CalcMethod.Separate && !downloadConfig?.date_types?.length) return false;
    return true;
  })();

  // const [isDataAmountMsgVisible, setIsDataAmountMsgVisible] = useState(false);

  // 是否只勾选了 只有global数据的指标
  const getIsOnlyGlobalDataMetrics = (metrics: string[]): boolean => {
    const metricsTemp = metrics?.filter((metric: string) => !ALL_ONLY_GLOBAL_DATA_METRICS?.includes(metric));
    return !(metricsTemp && metricsTemp?.length > 0);
  };

  // 判断当前勾选指标 是否 只有月粒度数据
  const getIsOnlyMonthlyDataMetrics = (metrics: string[]): boolean => {
    const metricsTemp = metrics?.filter((metric: string) => !ALL_ONLY_MONTHLY_DATA_METRICS?.includes(metric));
    return !(metricsTemp && metricsTemp?.length > 0);
  };

  const resetDownloadConfig = () => {
    const downloadConfigTemp = {
      metrics: [...metricsValues],
      release_date_show: false,
      company_show: false,
      genre_show: false,
      ratio_show: true,
      date_method: CalcMethod.Aggregated,
      date_types: ['daily'],
      region_method: CalcMethod.Aggregated,
      data_amount: 'export_all_data',
      data_amount_num: 0,
      file_format: '0',
    };
    setDownloadConfig(downloadConfigTemp);
  };

  const handleConfirm = (e: any) => {
    e?.stopPropagation();
    e?.preventDefault();
    handleDownloadLog(downloadLogConfigKey);
    onConfirm?.(downloadConfig);
    setVisible?.(false);
    setTimeout(() => {
      resetDownloadConfig();
    });
  };

  const handleCancel = (e: any) => {
    e?.stopPropagation();
    e?.preventDefault();
    onCancel?.();
    setVisible?.(false);
    setTimeout(() => {
      resetDownloadConfig();
    });
  };

  const handleDownloadLog = (logConfigKey?: string) => {
    // @ts-ignore
    const logConfig = dbiBtnLogMes[logConfigKey];
    if (!logConfig) return;
    buriedBtnPoint(logConfig);
  };

  useEffect(() => {
    if (!visible) return;
    const downloadDateTypes = getDownloadDateTypes(defaultDateTypes);
    setDownloadConfig({
      ...downloadConfig,
      date_types: downloadDateTypes,
    });
  }, [visible]);

  return (
    <Modal
      className={classnames('dbi-compare-pc-console-games-modal dbi-download-type dbi-download-type-two', {
        zh: isZH,
        en: isEN,
      })}
      visible={visible}
      size={820}
      caption={
        <div className="download-head">
          <span>{formatMessage({ id: 'dbi_download_settings' })}</span>
          <TipContext className="tip-text-hd" tipText={formatMessage({ id: 'dbi_compare_pc_console_download_tips' })} />
        </div>
      }
      onClose={() => {
        onClose?.();
      }}
    >
      <Modal.Body>
        <Form className="dbi-download-form teaui-form-alignment">
          <Form.Item label={formatMessage({ id: 'dbi_label_metrics' })} showStatusIcon={false}>
            <Checkbox.Group
              value={downloadConfig?.metrics}
              onChange={(value) => {
                if (!value?.length) return;
                const isOnlyGlobalDataMetrics = getIsOnlyGlobalDataMetrics(value);
                const isOnlyMonthlyDataMetrics = getIsOnlyMonthlyDataMetrics(value);
                let downloadConfigTemp = {
                  ...downloadConfig,
                  metrics: value,
                };
                // 当前地区设置为非聚合，并且只勾选了只有global数据的指标时，修改地区设置为 聚合
                if (downloadConfig?.region_method !== CalcMethod.Aggregated && isOnlyGlobalDataMetrics) {
                  downloadConfigTemp = {
                    ...downloadConfigTemp,
                    region_method: CalcMethod.Aggregated,
                  };
                }
                // 当前时间粒度设置为拆分，并且只勾选了只有月粒度数据的指标时，修改勾选的时间粒度为 只有月粒度选项
                if (downloadConfig.date_method === CalcMethod.Separate && isOnlyMonthlyDataMetrics) {
                  downloadConfigTemp = {
                    ...downloadConfigTemp,
                    date_types: ['monthly'],
                  };
                }
                setDownloadConfig(downloadConfigTemp);
              }}
            >
              {metricsOptions?.map((metricOption) => (
                <Checkbox key={metricOption?.value} name={metricOption?.value}>
                  <img className="data-source-icon" src={ metricOption?.icon} />
                  {formatMessageSafely({ id: metricOption?.text })}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbi_label_basic_info' })} showStatusIcon={false}>
            <Checkbox
              value={downloadConfig?.release_date_show}
              onChange={(value) => {
                const downloadConfigTemp = {
                  ...downloadConfig,
                  release_date_show: value,
                };
                setDownloadConfig(downloadConfigTemp);
              }}
            >
              {formatMessage({ id: 'dbi_label_release_date' })}
            </Checkbox>
            <Checkbox
              value={downloadConfig?.company_show}
              onChange={(value) => {
                const downloadConfigTemp = {
                  ...downloadConfig,
                  company_show: value,
                };
                setDownloadConfig(downloadConfigTemp);
              }}
            >
              {formatMessage({ id: 'dbi_label_company_info' })}
            </Checkbox>
            <Checkbox
              value={downloadConfig?.genre_show}
              onChange={(value) => {
                const downloadConfigTemp = {
                  ...downloadConfig,
                  genre_show: value,
                };
                setDownloadConfig(downloadConfigTemp);
              }}
            >
              {formatMessage({ id: 'dbi_label_genre' })}
            </Checkbox>
          </Form.Item>
          <Form.Item className='form-item--top-18' label={formatMessage({ id: 'dbi_label_data_calculation' })} showStatusIcon={false}>
            <Form>
              <Form.Item
                className="dbi-pc-console-compare-download-time-settings"
                label={formatMessage({ id: 'dbi_label_time_settings' })}
                showStatusIcon={false}
              >
                <RadioGroup
                  className={classnames({ 'dbi-radio-group': downloadConfig?.date_method === CalcMethod.Separate })}
                  value={downloadConfig?.date_method}
                  onChange={(value) => {
                    const downloadConfigTemp = {
                      ...downloadConfig,
                      date_method: value,
                    };
                    setDownloadConfig(downloadConfigTemp);
                  }}
                >
                  <Radio name={CalcMethod.Aggregated}>{formatMessage({ id: 'dbi_label_aggregated' })}</Radio>
                  <Radio name={CalcMethod.Separate}>{formatMessage({ id: 'dbi_label_separate_by_time' })}</Radio>
                </RadioGroup>
                {downloadConfig?.date_method === CalcMethod.Separate && (
                  <Checkbox.Group
                    className="dbi-pc-console-compare-download-time-settings-date-types"
                    value={downloadConfig?.date_types}
                    onChange={(value) => {
                      if (!value?.length) return;
                      const downloadConfigTemp = {
                        ...downloadConfig,
                        date_types: value,
                      };
                      setDownloadConfig(downloadConfigTemp);
                    }}
                  >
                    {/* 只勾选了只有月粒度数据的指标时，时间设置 只能为 aggregated，或者 按月拆分， 所以不需要展示 按日、按周拆分选项 */}
                    {!getIsOnlyMonthlyDataMetrics(downloadConfig?.metrics) && (
                      <>
                        <Checkbox name="daily">{formatMessage({ id: 'dbi_daily' })}</Checkbox>
                        <Checkbox name="weekly">{formatMessage({ id: 'dbi_weekly' })}</Checkbox>
                      </>
                    )}
                    <Checkbox name="monthly">{formatMessage({ id: 'dbi_monthly' })}</Checkbox>
                  </Checkbox.Group>
                )}
              </Form.Item>
              <Form.Item label={formatMessage({ id: 'dbi_label_region_settings' })} showStatusIcon={false}>
                <RadioGroup
                  value={downloadConfig?.region_method}
                  onChange={(value) => {
                    const downloadConfigTemp = {
                      ...downloadConfig,
                      region_method: value,
                    };
                    setDownloadConfig(downloadConfigTemp);
                  }}
                >
                  <Radio name={CalcMethod.Aggregated}>{formatMessage({ id: 'dbi_label_aggregated' })}</Radio>
                  {/* 只勾选了只有global数据的指标时，地区设置 只能为 aggregated，所以不需要展示 按国家、按地区拆分选项 */}
                  {!getIsOnlyGlobalDataMetrics(downloadConfig?.metrics) && (
                    <>
                      <Radio name="market">{formatMessage({ id: 'dbi_label_separate_by_country' })}</Radio>
                      <Radio name="region">{formatMessage({ id: 'dbi_label_separate_by_region' })}</Radio>
                    </>
                  )}
                </RadioGroup>
              </Form.Item>
              <Form.Item label={formatMessage({ id: 'dbi_label_ratio' })} showStatusIcon={false}>
                <Switch
                  className="dbi-common-switcher"
                  value={downloadConfig?.ratio_show}
                  onChange={(value) => {
                    const downloadConfigTemp = {
                      ...downloadConfig,
                      ratio_show: value,
                    };
                    setDownloadConfig(downloadConfigTemp);
                  }}
                />
              </Form.Item>
            </Form>
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbi_label_data_amount' })} showStatusIcon={false}>
            {/* <RadioGroup
              value={downloadConfig?.data_amount}
              onChange={(value) => {
                const downloadConfigTemp = {
                  ...downloadConfig,
                  data_amount: value,
                };
                setDownloadConfig(downloadConfigTemp);
              }}
            >
              <Radio name="export_all_data">{formatMessage({ id: 'dbi_export_all_data' })}</Radio>
              <Radio name="select_top">{formatMessage({ id: 'dbi_select_top' })}</Radio>
            </RadioGroup> */}
            <Form.Text>{formatMessage({ id: 'dbi_export_all_data' })}</Form.Text>
          </Form.Item>
          {/* {downloadConfig?.data_amount === 'select_top' && (
            <Form.Item
              label=""
              showStatusIcon={isDataAmountMsgVisible}
              status={isDataAmountMsgVisible ? 'error' : 'validating'}
            >
              <Bubble
                className="dbi-download-bubble"
                error
                visible={isDataAmountMsgVisible}
                placement="right"
                content={
                  <div className="dbi-download-tip">
                    <div className="dbi-download-tip-normal">
                      {formatMessage({ id: 'dbi_select_top_over_maximum_value' })}
                    </div>
                    <div className="dbi-download-tip-error">
                      {formatMessage({ id: 'dbi_select_top_must_more_than_one' })}
                    </div>
                  </div>
                }
              >
                <InputNumber
                  min={0}
                  max={total}
                  value={downloadConfig?.data_amount_num}
                  onChange={(value) => {
                    const downloadConfigTemp = {
                      ...downloadConfig,
                      data_amount_num: value,
                    };
                    setDownloadConfig(downloadConfigTemp);
                  }}
                  onFocus={() => {}}
                  onBlur={() => {}}
                />
              </Bubble>
            </Form.Item>
          )} */}
          <Form.Item label={formatMessage({ id: 'dbi_label_file_format' })} showStatusIcon={false}>
            <RadioGroup
              value={downloadConfig?.file_format}
              onChange={(value) => {
                const downloadConfigTemp = {
                  ...downloadConfig,
                  file_format: value,
                };
                setDownloadConfig(downloadConfigTemp);
              }}
            >
              <Radio name="0">{formatMessage({ id: 'dbi_label_excel' })}</Radio>
              <Radio name="1">{formatMessage({ id: 'dbi_label_csv' })}</Radio>
            </RadioGroup>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" disabled={!isDownloadConfigValid} onClick={handleConfirm}>
          {formatMessage({ id: 'dbi_confirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
